import { useEffect, useRef, useState } from 'react';
import prefersReducedMotion from 'utils/prefersReducedMotion';

export const COUNT_UP_DURATION_MS = 400;

const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

/**
 * The number to display for `value`: when `value` changes, it runs from the
 * figure on screen to the new one over ~400 ms, eased out. The first value is
 * shown as is, never counted up from 0, and so is any value under reduced
 * motion. Format what it returns as you would `value`.
 *
 * A value that is not a number (null while a list loads) is returned as is and
 * leaves the count where it was: the next number counts from there.
 */
const useCountUp = (value, { duration = COUNT_UP_DURATION_MS } = {}) => {
  const [displayed, setDisplayed] = useState(value);
  // What is on screen, mid-count included: a change during a count starts
  // from there instead of jumping back.
  const shownRef = useRef(value);

  useEffect(() => {
    if (!Number.isFinite(value)) return undefined;
    const from = shownRef.current;
    if (!Number.isFinite(from) || from === value || prefersReducedMotion()) {
      shownRef.current = value;
      setDisplayed(value);
      return undefined;
    }
    let frame;
    let start;
    const step = (now) => {
      if (start === undefined) start = now;
      const progress = Math.min((now - start) / duration, 1);
      const next =
        progress === 1 ? value : from + (value - from) * easeOutCubic(progress);
      shownRef.current = next;
      setDisplayed(next);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  // Until its effect runs, a first number after a non-number has nothing on
  // screen to count from.
  if (!Number.isFinite(value) || !Number.isFinite(displayed)) return value;
  return prefersReducedMotion() ? value : displayed;
};

export default useCountUp;
